import { Injectable, InjectionToken, inject } from "@angular/core";
import { OnDestroy, signal } from "@angular/core";
import * as signalR from "@microsoft/signalr";
import { Message } from "primeng/api";
import { MergeNotification, MergeNotificationStatus } from "../processing/merge-notification";

export const MergePatientProcessingEndpoint = new InjectionToken<string>("Merge Patient Processing endpoint");

export const UserMergePatientEndpoint = "api/merge-patients/processing";
export const PatientMergePatientEndpoint = "api/merge-patients/me/processing";
export const PendingMatchingProfileMergePatientEndpoint = "api/merge-patients/pending/processing";

@Injectable()
export class MergeProcessingService implements OnDestroy {
    private readonly _messages = signal<Message[]>([]);
    private readonly _running = signal(false);
    private readonly _notifications = signal<MergeNotification[]>([]);
    private readonly _status = signal<"idle" | "processing" | "success" | "error">("idle");
    private _listenersSetup = false;

    private readonly processingConnection = new signalR.HubConnectionBuilder()
        .withUrl(inject(MergePatientProcessingEndpoint))
        .configureLogging(signalR.LogLevel.Error)
        .withAutomaticReconnect()
        .build();

    readonly messages = this._messages.asReadonly();
    readonly running = this._running.asReadonly();
    readonly notifications = this._notifications.asReadonly();
    readonly status = this._status.asReadonly();

    /**
     * Starts the Merge by starting a (signalR) connection with the server
     * @param parentId It's the Patient that will remain after the Merge, and will get all `childId`'s data.
     * @param childId It's the Patient that will be deleted
     * @param authToken Optional. Needed in the cases that a Patient is going to Merge itself with another Patient
     * @returns A Promise that represents the asynchronous operation.
     */
    async start(parentId: string, childId: string, authToken: string = undefined) {
        if (!parentId || !childId) return;

        if (this.processingConnection.state !== signalR.HubConnectionState.Disconnected) {
            await this.shutdown();
        }

        await this.processingConnection.start();
        if (!this._listenersSetup) {
            this.setupNotificationListeners();
            this._listenersSetup = true;
        }

        const mergeArgs = [parentId, childId];
        if (authToken) {
            mergeArgs.push(authToken);
        }

        await this.processingConnection.send("Merge", ...mergeArgs);

        this._messages.update(_ => []);
        this._running.set(true);
        this._notifications.update(_ => []);
        this._status.set("processing");
    }

    ngOnDestroy() {
        return this.shutdown();
    }

    private setupNotificationListeners() {
        this.processingConnection.on("MergeValidationsComplete", () =>
            this.addMessage("All validations succeeded. Moving forward with the Merge.", "success")
        );
        this.processingConnection.on("MergeValidationsError", errorMessage => {
            this.addMessage(errorMessage, "error");
            this._running.set(false);
        });

        this.processingConnection.on("Hello", () =>
            this.addMessage(
                "The Merge will process in the background, so please feel free to navigate away from " +
                    "this page and continue using the system.",
                "success"
            )
        );
        this.processingConnection.on("MergeClinicsStarted", () => this.addNotification("Merging Appointments..."));
        this.processingConnection.on("MergeEpisodesStarted", () => this.addNotification("Merging Episodes..."));
        this.processingConnection.on("MergeFormDataStarted", () => this.addNotification("Merging Forms..."));
        this.processingConnection.on("MergeGrantedDataAccessStarted", () => this.addNotification("Merging Granted Data Access..."));
        this.processingConnection.on("MergeIm1EntitiesStarted", () => this.addNotification("Merging IM1 entities..."));
        this.processingConnection.on("MergeLoginsStarted", () => this.addNotification("Merging Logins (NHS/MiPro/IM1)..."));
        this.processingConnection.on("MergeNotesStarted", () => this.addNotification("Merging Notes..."));
        this.processingConnection.on("MergePrescriptionsStarted", () => this.addNotification("Merging Prescriptions..."));
        this.processingConnection.on("MergeWalkInListStarted", () => this.addNotification("Merging Daily Lists..."));
        this.processingConnection.on("AddFeedbackMessage", message => this.addFeedbackMessage(message));

        this.processingConnection.on("MergeComplete", () => {
            this.addMessage("Merge completed successfully", "success");
            this.setLastNotificationStatus("complete");
            this.finishMerge();
            this._status.set("success");
        });
        this.processingConnection.on("MergeError", error => {
            this.addMessage(error, "error");
            this.setLastNotificationStatus("error");
            this.finishMerge();
            this._status.set("error");
        });
    }

    private addMessage(message: string, severity: string) {
        this._messages.update(messages => [
            ...messages,
            <Message>{
                closable: true,
                detail: message,
                severity: severity
            }
        ]);
    }

    private addNotification(message: string) {
        this.setLastNotificationStatus("complete");
        const newNotification = new MergeNotification(message);
        this._notifications.update(notifications => [...notifications, newNotification]);
    }

    private addFeedbackMessage(feedbackMessage: string) {
        const lastNotification = this.notifications()[this.notifications().length - 1];
        if (lastNotification) {
            lastNotification.addFeedback(feedbackMessage);
        }
    }

    private setLastNotificationStatus(status: Exclude<MergeNotificationStatus, "processing">) {
        const lastNotification = this.notifications()[this.notifications().length - 1];
        if (lastNotification) {
            lastNotification.updateStatus(status);
        }
    }

    private finishMerge() {
        this._running.set(false);
        return this.shutdown();
    }

    private shutdown() {
        return this.processingConnection.stop();
    }
}
